import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
} from '@chakra-ui/react';

import { RefObject } from 'react';

import { Individual } from '@aslanpay/portal-common-lib';

import { formatDateToDDMMYYYY } from 'app/utils/date';

interface RehireConfirmationProps {
  previousEmployment: Individual | null;
  isOpen: boolean;
  isCreating: boolean;
  cancelRef: RefObject<HTMLButtonElement>;
  onConfirm: () => void;
  onReject: () => void;
  onCancel: () => void;
}

/**
 * Asks the admin to confirm that the employee they are creating is the person who left before.
 *
 * Advisory only — it never refuses the create. "Not the same person" goes ahead without the link, so a wrong
 * match can never block someone from being added; cancelling returns to the form with the values intact.
 *
 * The copy distinguishes a departure from a deleted record, because that is what tells the admin whether this is
 * the person returning or their own earlier cleanup.
 *
 * Any date on the record is a leave date whatever its status, so it is always worded as one. A LEFT record always
 * carries one; a DELETED record carries one only when it was terminated before being deleted, so the clause is
 * dropped rather than quoting a date the record does not have.
 */
function RehireConfirmation({
  previousEmployment,
  isOpen,
  isCreating,
  cancelRef,
  onConfirm,
  onReject,
  onCancel,
}: RehireConfirmationProps) {
  if (!previousEmployment) {
    return null;
  }

  const fullName = `${previousEmployment.firstName} ${previousEmployment.lastName}`;

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onCancel} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Has this employee worked here before?
          </AlertDialogHeader>

          <AlertDialogBody>
            <Text>
              <strong>{fullName}</strong>{' '}
              {previousEmployment.status === 'DELETED'
                ? 'matches an employment whose record was deleted'
                : 'matches an employment that ended'}
              {previousEmployment.terminationDate && (
                <>
                  {', left on '}
                  <strong>{formatDateToDDMMYYYY(previousEmployment.terminationDate)}</strong>
                </>
              )}
              .
            </Text>
            <Text mt="12px">
              Confirm to create this employee as a new record linked to that one. If this is someone
              else who happens to share those details, create them without the link. Cancel to go
              back and check what you entered.
            </Text>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onCancel} variant="outline" isDisabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={onReject} ml="12px" variant="outline" isDisabled={isCreating}>
              Not the same person
            </Button>
            <Button onClick={onConfirm} ml="12px" isLoading={isCreating}>
              Yes, this is a rehire
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

export default RehireConfirmation;
